// Campaigns.jsx — Track marketing campaigns + measure post-campaign retention.
import React, { useEffect, useState, useCallback } from "react";
import { api, formatErr } from "./lib/api";
import { useAuth } from "./lib/auth";
import { confirmWithUndo, showToast } from "./lib/theme";
import { downloadCSV } from "./lib/export";
import { AssigneeSelect } from "./lib/users";
import CampaignsAIReview from "./CampaignsAIReview";
import CodeImpactCard from "./CodeImpactCard";
import WeeklyRoiReportCard from "./WeeklyRoiReportCard";
import MarketingPerformancePanel from "./MarketingPerformancePanel";
import PlanCampaignsSection from "./PlanCampaignsSection";
import DateField from "./components/DateField";

const TYPE_LABELS = {
  cup_59: "🎟 الـ 59 كوب",
  loyalty: "💖 ولاء",
  discount: "💰 خصم",
  social: "📱 سوشل ميديا",
  event: "🎉 فعالية",
  happy_hour: "🕔 ساعة السعادة",
  general: "📌 عام",
};

const REWARD_TYPES = [
  { v: "discount",      label: "💰 خصم %", placeholder: "20" },
  { v: "double_points", label: "✨ نقاط مضاعفة (Bonat)", placeholder: "x2 / x3" },
  { v: "free_product",  label: "🎁 منتج مجاني", placeholder: "اسم المنتج" },
  { v: "other",         label: "🔖 أخرى", placeholder: "صف المكافأة..." },
];

const WEEKDAYS_AR = [
  { id: 0, lbl: "الأحد" }, { id: 1, lbl: "الإثنين" }, { id: 2, lbl: "الثلاثاء" },
  { id: 3, lbl: "الأربعاء" }, { id: 4, lbl: "الخميس" }, { id: 5, lbl: "الجمعة" },
  { id: 6, lbl: "السبت" },
];

const STATUS_BADGES = {
  active: { lbl: "🟢 نشطة", color: "var(--success)" },
  ended: { lbl: "⏹ منتهية", color: "var(--muted)" },
  archived: { lbl: "📦 مؤرشفة", color: "var(--brown)" },
};

const SEGMENT_OPTS = [
  { v: "", label: "— يدوي / لا تحديد تلقائي —" },
  { v: "vip", label: "💎 كبار المنفقين (VIP)" },
  { v: "loyal", label: "✨ أوفياء (6+ زيارات)" },
  { v: "highavg", label: "📈 عالي الإنفاق/زيارة" },
  { v: "regular", label: "👤 عاديون" },
  { v: "new", label: "🆕 عملاء جدد" },
  { v: "atrisk", label: "⚠ في خطر" },
  { v: "lost", label: "💔 فقدناهم" },
  { v: "all", label: "👥 كل العملاء المسجّلين" },
];

function fm(n) { return Math.round(Number(n) || 0).toLocaleString("en-US"); }

export default function Campaigns() {
  const { user } = useAuth();
  const canCreate = true;
  const canEdit = user.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "", type: "general", start_date: "", end_date: "",
    description: "", target_segment: "", cost_sar: 0, notes: "",
    owner: "", due_date: "",
    // Happy-hour specific: list of windows. Each = {hour_start, hour_end, days_of_week}.
    // Allows e.g. "5-6pm Sun/Tue/Thu + 8-9pm Mon/Wed/Fri" on the same campaign.
    windows: [
      { hour_start: "17:00", hour_end: "18:00", days_of_week: [0, 1, 2, 3, 4, 5, 6] },
    ],
    reward_type: "discount", reward_value: "20",
    min_invoice_amount: 0,
    audience_scope: "all",
  });
  const [expanded, setExpanded] = useState(null);
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);
  // When set, the form acts as an editor for this existing campaign id
  // (PATCH instead of POST). Cleared after a successful save or cancel.
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const blankForm = () => ({
    name: "", type: "general", start_date: "", end_date: "",
    description: "", target_segment: "", cost_sar: 0, notes: "",
    owner: "", due_date: "",
    windows: [{ hour_start: "17:00", hour_end: "18:00", days_of_week: [0, 1, 2, 3, 4, 5, 6] }],
    reward_type: "discount", reward_value: "20",
    min_invoice_amount: 0, audience_scope: "all",
  });

  const startEdit = (c) => {
    // Normalize an existing campaign back into the form shape. Older docs
    // may have a single hour_start/hour_end pair (legacy) — wrap that into
    // the new `windows` list so the editor renders the rows correctly.
    let windows = Array.isArray(c.windows) && c.windows.length ? c.windows : null;
    if (!windows) {
      windows = [{
        hour_start: c.hour_start || "17:00",
        hour_end: c.hour_end || "18:00",
        days_of_week: c.days_of_week || [0, 1, 2, 3, 4, 5, 6],
      }];
    }
    setForm({
      name: c.name || "",
      type: c.type || "general",
      start_date: (c.start_date || "").slice(0, 10),
      end_date: (c.end_date || "").slice(0, 10),
      description: c.description || "",
      target_segment: c.target_segment || "",
      cost_sar: c.cost_sar || 0,
      notes: c.notes || "",
      owner: c.owner || "",
      due_date: (c.due_date || "").slice(0, 10),
      windows,
      reward_type: c.reward_type || "discount",
      reward_value: c.reward_value || "20",
      min_invoice_amount: c.min_invoice_amount || 0,
      audience_scope: c.audience_scope || "all",
    });
    setEditingId(c.id);
    setShowForm(true);
    // Scroll the form into view so the CEO sees what they're editing.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="campaign-form"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const cancelEdit = () => { setEditingId(null); setForm(blankForm()); setShowForm(false); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/campaigns/${editingId}`, form);
        showToast({ kind: "success", message: `✅ تم تحديث حملة "${form.name}"` });
      } else {
        await api.post("/campaigns", form);
        showToast({ kind: "success", message: `✅ تم إنشاء حملة "${form.name}"` });
      }
      setEditingId(null);
      setForm(blankForm());
      setShowForm(false);
      load();
    } catch (e) {
      showToast({ kind: "danger", message: formatErr(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const loadImpact = async (c) => {
    if (expanded === c.id) { setExpanded(null); setImpact(null); return; }
    setExpanded(c.id); setImpact(null);
    try {
      const { data } = await api.get(`/campaigns/${c.id}/impact`);
      setImpact(data);
    } catch (e) {
      showToast({ kind: "danger", message: formatErr(e.response?.data?.detail) });
    }
  };

  const remove = (c) => {
    setItems((cur) => cur.filter((x) => x.id !== c.id));
    confirmWithUndo({
      message: `🗑 سيتم حذف حملة "${c.name}"`,
      onConfirm: async () => { try { await api.delete(`/campaigns/${c.id}`); } catch (_e) { /* keep undo */ } load(); },
      onUndo: load,
    });
  };

  const updateStatus = async (c, status) => {
    await api.patch(`/campaigns/${c.id}`, { status });
    load();
  };

  const exportImpact = (kind) => {
    if (!impact) return;
    const rows = (kind === "continued" ? impact.continued : impact.churned).map((r) => [
      r.name, r.phone, r.during_visits, r.during_spend, r.after_visits, r.after_spend, r.last_visit || "—",
    ]);
    downloadCSV(`حملة_${impact.campaign.name}_${kind}`, ["الاسم", "الجوال", "زيارات أثناء", "إنفاق أثناء", "زيارات بعد", "إنفاق بعد", "آخر زيارة"], rows);
  };

  return (
    <div data-testid="campaigns-page" style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* v121 — real ROI per campaign code */}
      <CodeImpactCard />
      <WeeklyRoiReportCard />
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div>
            <h3 style={{ margin: "0 0 4px", color: "var(--brown-deep)" }}>🎯 الحملات التسويقية</h3>
            <p style={{ margin: 0, fontSize: 12, color: "var(--muted)", lineHeight: 1.8 }}>
              سجّل كل حملاتك (الـ 59 كوب، خصومات، فعاليات...) وقِس تأثيرها بعد انتهائها: من استمر معك ومن توقّف.
            </p>
          </div>
          {canCreate && (
            <button type="button" className="btn btn-primary"
              onClick={() => { if (showForm) { cancelEdit(); } else { setShowForm(true); } }}
              data-testid="toggle-campaign-form">
              {showForm ? "إلغاء" : "+ حملة جديدة"}
            </button>
          )}
        </div>
      </div>

      <CampaignsAIReview />

      {/* v119.124: weekly marketing performance dashboard */}
      <MarketingPerformancePanel />

      {/* v119.123: campaigns generated from approved marketing plan items */}
      <PlanCampaignsSection />

      {showForm && canCreate && (
        <form className="card" onSubmit={submit} data-testid="campaign-form" style={{ marginBottom: 14 }}>
          <h4 style={{ margin: "0 0 10px", color: "var(--brown-deep)" }} data-testid="campaign-form-title">
            {editingId ? "✏ تعديل الحملة" : "➕ حملة جديدة"}
          </h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
            <div><label style={{ fontSize: 11 }}>اسم الحملة *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="camp-name" style={{ width: "100%" }} /></div>
            <div><label style={{ fontSize: 11 }}>النوع</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="camp-type" style={{ width: "100%" }}>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11 }}>تاريخ البدء *</label><DateField  required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="camp-start" style={{ width: "100%" }} /></div>
            <div><label style={{ fontSize: 11 }}>تاريخ الانتهاء *</label><DateField  required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="camp-end" style={{ width: "100%" }} /></div>
            <div><label style={{ fontSize: 11 }}>الجمهور المستهدف (تعبئة تلقائية)</label>
              <select value={form.target_segment} onChange={(e) => setForm({ ...form, target_segment: e.target.value })} data-testid="camp-segment" style={{ width: "100%" }}>
                {SEGMENT_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11 }}>التكلفة (ريال)</label><input type="number" min={0} value={form.cost_sar} onChange={(e) => setForm({ ...form, cost_sar: Number(e.target.value) || 0 })} data-testid="camp-cost" style={{ width: "100%" }} /></div>
            <AssigneeSelect value={form.owner} onChange={(v) => setForm({ ...form, owner: v })} label="👤 المسؤول عن الحملة" testid="camp-owner" />
            <div><label style={{ fontSize: 11 }}>📅 موعد المتابعة</label><DateField  value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="camp-due-date" style={{ width: "100%" }} /></div>
          </div>
          <div style={{ marginTop: 8 }}><label style={{ fontSize: 11 }}>الوصف</label><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="camp-desc" style={{ width: "100%" }} /></div>

          {/* Happy-Hour-specific panel — appears only when type === "happy_hour" */}
          {form.type === "happy_hour" && (
            <div data-testid="camp-happy-hour-panel" style={{ marginTop: 12, padding: 12,
                background: "linear-gradient(135deg, #FEF3C7, #FED7AA)", borderRadius: 8,
                border: "1px solid #FCD34D" }}>
              <h5 style={{ margin: "0 0 8px", color: "#92400E", fontSize: 13 }}>🕔 إعدادات ساعة السعادة</h5>

              {/* Reward (common to all windows) */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700 }}>🎁 نوع المكافأة</label>
                  <select value={form.reward_type} required
                    onChange={(e) => setForm({ ...form, reward_type: e.target.value })}
                    data-testid="hh-reward-type" style={{ width: "100%" }}>
                    {REWARD_TYPES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700 }}>💰 قيمة المكافأة</label>
                  <input value={form.reward_value} required
                    placeholder={REWARD_TYPES.find((r) => r.v === form.reward_type)?.placeholder || ""}
                    onChange={(e) => setForm({ ...form, reward_value: e.target.value })}
                    data-testid="hh-reward-value" style={{ width: "100%" }} />
                </div>
              </div>

              {/* Multiple windows */}
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <label style={{ fontSize: 11.5, fontWeight: 700, color: "#92400E" }}>
                    🕔 نوافذ السعادة ({form.windows.length})
                  </label>
                  <button type="button" data-testid="hh-add-window"
                    onClick={() => setForm({
                      ...form,
                      windows: [...form.windows, { hour_start: "17:00", hour_end: "18:00", days_of_week: [0, 1, 2, 3, 4, 5, 6] }],
                    })}
                    style={{ padding: "4px 10px", borderRadius: 16, cursor: "pointer", background: "#92400E", color: "#fff",
                      border: "none", fontSize: 11, fontWeight: 700 }}>
                    ➕ أضف نافذة
                  </button>
                </div>
                <p style={{ fontSize: 10.5, color: "#78350F", margin: "0 0 8px", lineHeight: 1.6 }}>
                  💡 يمكنك تشغيل ساعات/أيام مختلفة في نفس الحملة. مثال: 5-6م الأحد/الثلاثاء + 8-9م الإثنين/الأربعاء.
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {form.windows.map((w, idx) => (
                    <div key={idx} data-testid={`hh-window-${idx}`}
                      style={{ padding: 10, background: "rgba(255,255,255,0.7)", borderRadius: 8, border: "1px solid #FCD34D" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <span style={{ fontSize: 11.5, fontWeight: 800, color: "#92400E" }}>نافذة #{idx + 1}</span>
                        {form.windows.length > 1 && (
                          <button type="button" data-testid={`hh-remove-window-${idx}`}
                            onClick={() => setForm({
                              ...form,
                              windows: form.windows.filter((_, i) => i !== idx),
                            })}
                            style={{ padding: "2px 8px", borderRadius: 12, cursor: "pointer", background: "var(--danger)", color: "#fff",
                              border: "none", fontSize: 10, fontWeight: 700 }}>
                            🗑 حذف
                          </button>
                        )}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
                        <div>
                          <label style={{ fontSize: 10.5 }}>من</label>
                          <input type="time" value={w.hour_start} required
                            data-testid={`hh-window-${idx}-start`}
                            onChange={(e) => setForm({
                              ...form,
                              windows: form.windows.map((x, i) => i === idx ? { ...x, hour_start: e.target.value } : x),
                            })}
                            style={{ width: "100%" }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 10.5 }}>إلى</label>
                          <input type="time" value={w.hour_end} required
                            data-testid={`hh-window-${idx}-end`}
                            onChange={(e) => setForm({
                              ...form,
                              windows: form.windows.map((x, i) => i === idx ? { ...x, hour_end: e.target.value } : x),
                            })}
                            style={{ width: "100%" }} />
                        </div>
                      </div>
                      <div>
                        <label style={{ fontSize: 10.5, marginBottom: 3, display: "block" }}>📅 الأيام السارية</label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                          {WEEKDAYS_AR.map((d) => {
                            const active = w.days_of_week.includes(d.id);
                            return (
                              <button key={d.id} type="button"
                                data-testid={`hh-window-${idx}-day-${d.id}`}
                                onClick={() => setForm({
                                  ...form,
                                  windows: form.windows.map((x, i) => i === idx ? {
                                    ...x,
                                    days_of_week: active
                                      ? x.days_of_week.filter((y) => y !== d.id)
                                      : [...x.days_of_week, d.id].sort(),
                                  } : x),
                                })}
                                style={{
                                  padding: "4px 8px", borderRadius: 14, cursor: "pointer",
                                  background: active ? "#92400E" : "#fff",
                                  color: active ? "#fff" : "#92400E",
                                  border: "1px solid #92400E", fontSize: 10.5, fontWeight: 700,
                                }}>
                                {d.lbl}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: 10, padding: 8, background: "rgba(255,255,255,0.6)", borderRadius: 6 }}>
                <h6 style={{ margin: "0 0 6px", fontSize: 11, color: "#92400E" }}>🎯 توسيع الجمهور (اختياري)</h6>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 10.5 }}>نوع العميل</label>
                    <select value={form.audience_scope}
                      onChange={(e) => setForm({ ...form, audience_scope: e.target.value })}
                      data-testid="hh-audience-scope" style={{ width: "100%", fontSize: 11 }}>
                      <option value="all">👥 الجميع (داخل النوافذ)</option>
                      <option value="new">🆕 العملاء الجدد فقط</option>
                      <option value="returning">🔁 العملاء المتكررين فقط</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 10.5 }}>تصنيف العميل</label>
                    <select value={form.target_segment}
                      onChange={(e) => setForm({ ...form, target_segment: e.target.value })}
                      style={{ width: "100%", fontSize: 11 }}>
                      <option value="">— كل التصنيفات —</option>
                      {SEGMENT_OPTS.filter((o) => o.v && o.v !== "all").map((o) => (
                        <option key={o.v} value={o.v}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 10.5 }}>الحد الأدنى للفاتورة (ر)</label>
                    <input type="number" min={0} value={form.min_invoice_amount}
                      onChange={(e) => setForm({ ...form, min_invoice_amount: Number(e.target.value) || 0 })}
                      data-testid="hh-min-amount" style={{ width: "100%", fontSize: 11 }} />
                  </div>
                </div>
              </div>

              <p style={{ fontSize: 10, color: "#78350F", marginTop: 8, marginBottom: 0, lineHeight: 1.7 }}>
                💡 الجمهور المستهدف = أي فاتورة تقع داخل أيٍّ من النوافذ المُحدّدة. سيتم قياس 4 KPIs إجمالية + تفصيل لكل نافذة.
              </p>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button type="submit" disabled={busy} className="btn btn-success" data-testid="camp-submit">
              {busy ? "..." : (editingId ? "💾 حفظ التعديلات" : "💾 احفظ الحملة")}
            </button>
            {editingId && (
              <button type="button" onClick={cancelEdit} className="btn" data-testid="camp-cancel-edit"
                style={{ background: "#fff", border: "1px solid var(--border)" }}>
                إلغاء التعديل
              </button>
            )}
          </div>
          <p style={{ fontSize: 10, color: "var(--muted)", marginTop: 8 }}>
            💡 سيتم تعبئة قائمة العملاء المستهدفين تلقائياً من ملف العملاء حسب التصنيف المختار. يمكنك تعديلها لاحقاً.
          </p>
        </form>
      )}

      {loading ? <div style={{ padding: 20, textAlign: "center" }}>⏳</div> : items.length === 0 ? (
        <div className="card" style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>
          لا توجد حملات بعد. أنشئ أول حملة لتتبّع تأثيرها على العملاء.
        </div>
      ) : items.map((c) => {
        const status = STATUS_BADGES[c.status] || STATUS_BADGES.active;
        return (
          <div key={c.id} className="card" style={{ marginBottom: 10 }} data-testid={`camp-${c.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <div>
                <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>{TYPE_LABELS[c.type] || "📌"} {c.name}</h4>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                  📅 {c.start_date} → {c.end_date} · 👥 {fm(c.targeted_phones?.length || 0)} عميل مستهدف
                  {c.cost_sar > 0 && ` · 💵 ${fm(c.cost_sar)} ر`}
                  {c.owner && <> · 👤 <b style={{ color: "var(--brown-deep)" }}>{c.owner}</b></>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ padding: "3px 8px", background: status.color, color: "#fff", borderRadius: 4, fontSize: 11 }}>{status.lbl}</span>
                <button type="button" className="btn btn-soft" onClick={() => loadImpact(c)} data-testid={`camp-impact-${c.id}`} style={{ fontSize: 11 }}>
                  {expanded === c.id ? "🔼 إخفاء" : "📊 قياس التأثير"}
                </button>
                {canEdit && (
                  <>
                    <select value={c.status || "active"} onChange={(e) => updateStatus(c, e.target.value)} style={{ fontSize: 11 }} data-testid={`camp-status-${c.id}`}>
                      {Object.entries(STATUS_BADGES).map(([k, v]) => <option key={k} value={k}>{v.lbl}</option>)}
                    </select>
                    <button type="button" onClick={() => startEdit(c)} className="btn btn-soft" data-testid={`camp-edit-${c.id}`} style={{ fontSize: 11 }}>✏ تعديل</button>
                    <button type="button" onClick={() => remove(c)} className="btn" data-testid={`camp-delete-${c.id}`} style={{ background: "var(--danger)", color: "#fff", fontSize: 11 }}>🗑</button>
                  </>
                )}
              </div>
            </div>
            {c.description && <p style={{ margin: "6px 0 0", fontSize: 12 }}>{c.description}</p>}

            {expanded === c.id && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed var(--border)" }}>
                {!impact ? <div style={{ textAlign: "center", color: "var(--muted)" }} data-testid={`hh-loading-${c.id}`}>⏳ جاري الحساب...</div> : c.type === "happy_hour" && impact.happy_hour ? (
                  <HappyHourImpactView impact={impact} c={c} />
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginBottom: 10 }}>
                      <div className="card" style={{ padding: 8, textAlign: "center" }}><div style={{ fontSize: 22, fontWeight: 800, color: "var(--info)" }}>{fm(impact.totals.targeted)}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>عدد المستهدفين</div></div>
                      <div className="card" style={{ padding: 8, textAlign: "center" }}><div style={{ fontSize: 22, fontWeight: 800, color: "var(--success)" }}>{fm(impact.totals.continued)}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>✅ استمروا</div></div>
                      <div className="card" style={{ padding: 8, textAlign: "center" }}><div style={{ fontSize: 22, fontWeight: 800, color: "var(--danger)" }}>{fm(impact.totals.churned)}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>❌ لم يستمروا</div></div>
                      <div className="card" style={{ padding: 8, textAlign: "center" }}><div style={{ fontSize: 22, fontWeight: 800, color: impact.totals.retention_pct >= 50 ? "var(--success)" : "var(--warn)" }}>{impact.totals.retention_pct}%</div><div style={{ fontSize: 11, color: "var(--muted)" }}>📊 نسبة الاستمرار</div></div>
                      <div className="card" style={{ padding: 8, textAlign: "center" }}><div style={{ fontSize: 18, fontWeight: 700 }}>{fm(impact.totals.total_post_spend)} ر</div><div style={{ fontSize: 11, color: "var(--muted)" }}>💰 الإنفاق بعد</div></div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <div style={{ background: "var(--success)", color: "#fff", padding: "6px 10px", borderRadius: 4, marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                          <span>✅ استمروا ({fm(impact.continued.length)})</span>
                          <button onClick={() => exportImpact("continued")} className="btn btn-soft" style={{ fontSize: 10, padding: "2px 8px" }}>📥</button>
                        </div>
                        <div style={{ maxHeight: 240, overflowY: "auto", fontSize: 12 }}>
                          {impact.continued.slice(0, 30).map((r, i) => (
                            <div key={i} style={{ padding: 4, borderBottom: "1px solid var(--border)" }}>
                              <b>{r.name}</b> · {r.after_visits} زيارة · {fm(r.after_spend)} ر
                            </div>
                          ))}
                          {impact.continued.length > 30 && <div style={{ padding: 4, color: "var(--muted)" }}>+ {impact.continued.length - 30} غيرهم...</div>}
                        </div>
                      </div>
                      <div>
                        <div style={{ background: "var(--danger)", color: "#fff", padding: "6px 10px", borderRadius: 4, marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                          <span>❌ لم يستمروا ({fm(impact.churned.length)})</span>
                          <button onClick={() => exportImpact("churned")} className="btn btn-soft" style={{ fontSize: 10, padding: "2px 8px" }}>📥</button>
                        </div>
                        <div style={{ maxHeight: 240, overflowY: "auto", fontSize: 12 }}>
                          {impact.churned.slice(0, 30).map((r, i) => (
                            <div key={i} style={{ padding: 4, borderBottom: "1px solid var(--border)" }}>
                              <b>{r.name}</b> · {r.during_visits} زيارة أثناء · {fm(r.during_spend)} ر
                            </div>
                          ))}
                          {impact.churned.length > 30 && <div style={{ padding: 4, color: "var(--muted)" }}>+ {impact.churned.length - 30} غيرهم...</div>}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Happy-Hour impact panel — 4 KPIs + audience breakdown + retention cohort.
// Reads the backend response shape returned by /campaigns/{id}/impact when
// the campaign type is "happy_hour".
// ─────────────────────────────────────────────────────────────────────────
function HappyHourImpactView({ impact, c }) {
  const hh = impact.happy_hour || {};
  const upliftPositive = (hh.uplift_pct ?? 0) > 0;
  // Resolve windows: prefer new multi-window list, fall back to legacy single-window doc.
  const windows = Array.isArray(c.windows) && c.windows.length > 0
    ? c.windows
    : c.hour_start
      ? [{ hour_start: c.hour_start, hour_end: c.hour_end, days_of_week: c.days_of_week }]
      : [];
  return (
    <div data-testid={`hh-impact-${c.id}`}>
      {/* Configuration recap — one chip per window */}
      <div style={{ padding: 10, background: "var(--beige)", borderRadius: 6, marginBottom: 12, fontSize: 12, lineHeight: 1.8 }}>
        🎁 المكافأة: <b>{(REWARD_TYPES.find((r) => r.v === c.reward_type)?.label || c.reward_type)} ({c.reward_value})</b>
        {c.min_invoice_amount > 0 && <> · 💰 حد أدنى: <b>{fm(c.min_invoice_amount)} ر</b></>}
        <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {windows.map((w, i) => (
            <span key={i} data-testid={`hh-window-chip-${c.id}-${i}`}
              style={{ background: "#fff", border: "1px solid #FCD34D", padding: "3px 8px", borderRadius: 12, fontSize: 11 }}>
              🕔 #{i + 1}: {w.hour_start} → {w.hour_end}
              {Array.isArray(w.days_of_week) && w.days_of_week.length < 7 && (
                <> · {w.days_of_week.map((d) => WEEKDAYS_AR.find((x) => x.id === d)?.lbl).join("،")}</>
              )}
            </span>
          ))}
        </div>
      </div>

      {/* 4 main KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8, marginBottom: 12 }}>
        <div className="card" style={{ padding: 10, textAlign: "center", borderTop: "3px solid var(--info)" }}
          data-testid={`hh-kpi-window-invoices-${c.id}`}>
          <div style={{ fontSize: 26, fontWeight: 800, color: "var(--info)" }}>{fm(hh.window_invoices)}</div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--brown-deep)" }}>📈 فواتير النافذة</div>
          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
            {fm(hh.window_revenue)} ر · {fm(hh.window_unique_customers)} عميل
          </div>
        </div>
        <div className="card" style={{ padding: 10, textAlign: "center", borderTop: `3px solid ${upliftPositive ? "var(--success)" : "var(--danger)"}` }}
          data-testid={`hh-kpi-uplift-${c.id}`}>
          <div style={{ fontSize: 26, fontWeight: 800, color: upliftPositive ? "var(--success)" : "var(--danger)" }}>
            {upliftPositive ? "+" : ""}{Number(hh.uplift_pct || 0).toFixed(1)}%
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--brown-deep)" }}>🔥 نسبة الزيادة (Uplift)</div>
          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
            مقابل نفس النافذة قبل الحملة
          </div>
        </div>
        <div className="card" style={{ padding: 10, textAlign: "center", borderTop: "3px solid var(--gold-deep)" }}
          data-testid={`hh-kpi-new-customers-${c.id}`}>
          <div style={{ fontSize: 26, fontWeight: 800, color: "var(--gold-deep)" }}>{fm(hh.new_customers_in_window)}</div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--brown-deep)" }}>👤 عملاء جدد</div>
          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
            أول فاتورة لهم داخل النافذة
          </div>
        </div>
        <div className="card" style={{ padding: 10, textAlign: "center", borderTop: "3px solid var(--brown-deep)" }}
          data-testid={`hh-kpi-return-rate-${c.id}`}>
          <div style={{ fontSize: 26, fontWeight: 800, color: "var(--brown-deep)" }}>
            {Number(hh.return_rate_pct || 0).toFixed(1)}%
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--brown-deep)" }}>🔁 معدّل العودة</div>
          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
            {fm(hh.returned_customers)} من {fm(hh.window_unique_customers)} رجع خلال 30 يوم
          </div>
        </div>
      </div>

      {/* Baseline comparison row */}
      <div style={{ padding: 10, background: "rgba(245,158,11,0.06)", border: "1px solid #FDE68A",
          borderRadius: 6, marginBottom: 10, fontSize: 12, lineHeight: 1.8 }}
        data-testid={`hh-baseline-${c.id}`}>
        <b style={{ color: "#92400E" }}>📊 المقارنة المرجعية:</b><br />
        أثناء الحملة: <b>{fm(hh.window_invoices)} فاتورة</b> ({fm(hh.window_revenue)} ر)
        ⇆ قبل الحملة: <b>{fm(hh.baseline_invoices)} فاتورة</b> ({fm(hh.baseline_revenue)} ر)
      </div>

      {/* Per-window breakdown — only when multi-window */}
      {Array.isArray(hh.per_window) && hh.per_window.length > 1 && (
        <div data-testid={`hh-per-window-${c.id}`} style={{ marginBottom: 10 }}>
          <h6 style={{ margin: "0 0 6px", fontSize: 12, color: "var(--brown-deep)" }}>📊 تفصيل لكل نافذة:</h6>
          <div style={{ display: "grid", gap: 6, gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
            {hh.per_window.map((w, i) => (
              <div key={i} data-testid={`hh-per-window-${c.id}-${i}`}
                style={{ padding: 8, border: "1px solid var(--border)", borderRadius: 6, background: "#fff" }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: "var(--brown-deep)" }}>
                  🕔 #{i + 1}: {w.hour_start} → {w.hour_end}
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "var(--info)", marginTop: 4 }}>
                  {fm(w.invoices)} فاتورة · {fm(w.revenue)} ر
                </div>
                <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
                  {fm(w.unique_customers)} عميل · Uplift {Number(w.uplift_pct || 0).toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer guidance */}
      <p style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.7, margin: 0 }}>
        💡 الأرقام تُحسب تلقائياً من فواتيرك حسب الفترة + النافذة الزمنية + الأيام. لربط نقاط Bonat لاحقاً، رفع ملف الـredemptions يفعّل تتبّع استخدام النقاط فعلياً.
      </p>
    </div>
  );
}
